import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { LIBRARY } from '@app/advanced-report/library';
import { ChartContextType, CultevaIndexChartTypes, SearchGroup, Status } from '@core/data';
import { IndexComponentData } from '@core/data/types/advanced-report';
import { getDefaultIndexConfig, IndexDataPoint, IndexDataPointType, setIndexConfigOptions } from '@core/data/types/Reports/report-indexes';
import { Snackbar } from '@core/material';
import { Characteristic, Index, Library } from '@library';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { InitReportIndexForm, ReportIndexFormState, ReportIndexFormStateModel } from './report-index-edit-form.state';

export interface ReportIndexEditFormDialogData {
    reportKey: string;
    ownerOrgKey: string;
    data: IndexComponentData;
}

interface ChartOption {
    value: string,
    label: string,
    icon: string,
}

@Component({
    selector: 'pv-report-index-edit-form-dialog',
    templateUrl: 'report-index-edit-form.dialog.html',
    styleUrls:   ['report-index-edit-form.dialog.scss'],
    preserveWhitespaces: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'pv-report-index-edit-form-dialog',
    }
})
export class ReportIndexEditFormDialog implements OnInit, OnDestroy {

    @Select(ReportIndexFormState)
    state$: Observable<ReportIndexFormStateModel>;

    @Select(ReportIndexFormState.characteristics)
    characteristics$: Observable<SearchGroup<Characteristic>[]>;

    @Select(ReportIndexFormState.indexes)
    indexes$: Observable<SearchGroup<Index>[]>;

    readonly Status = Status;

    labelControl: FormControl = new FormControl(null);
    typeControl: FormControl = new FormControl('radar', [Validators.required]);
    contextControl: FormControl = new FormControl(null, [Validators.required]);
    configControl: FormControl = new FormControl(null, [Validators.required]);
    charsControl: FormControl = new FormControl([], [Validators.required]);
    indexesControl: FormControl = new FormControl([], [Validators.required]);
    dataPointsArray: FormArray = new FormArray([], [Validators.required, Validators.minLength(3), Validators.maxLength(10)]);

    optionsGroup = new FormGroup ({
        chars: this.charsControl,
        indexes: this.indexesControl,
    });

    formGroup: FormGroup = new FormGroup({
        label: this.labelControl,
        type: this.typeControl,
        context: this.contextControl,
        indexConfig: this.configControl,
        dataPoints: this.dataPointsArray,
    });

    chartTypeOptions: ChartOption[];

    chartContextOptions = [
        {value: ChartContextType.CULTIVAR, label: 'Cultivar'},
        {value: ChartContextType.SITE, label: 'Site'},
        {value: ChartContextType.SAMPLE, label: 'Sample'},
        {value: ChartContextType.EVALUATION, label: 'Evaluation'},
    ];

    chartTypeRegistry: Partial<CultevaIndexChartTypes> = {
        radar: null,
        bar: null,
    };

    showRequired: boolean = false;

    private _destroy$ = new Subject();

    constructor(
        @Inject(LIBRARY) private _library: Library,
        private _dialogRef: MatDialogRef<ReportIndexEditFormDialogData>,
        @Inject(MAT_DIALOG_DATA) private _data: ReportIndexEditFormDialogData,
        private _snackbar: Snackbar,
        private _store: Store,
    ) {}

    ngOnInit() {
        const { reportKey, ownerOrgKey, data } = this._data;

        this._store.dispatch(new InitReportIndexForm(reportKey, ownerOrgKey, data));

        this.chartTypeOptions = Object.keys(this.chartTypeRegistry).map((option) => {
            let label = this.toTitleCase(option);
            return { value: option, label: this.toTitleCase(label), icon: this.getChartIconString(option) }
        });

        this.state$.pipe(takeUntil(this._destroy$))
            .subscribe((model: ReportIndexFormStateModel) => {
                if (model.status !== Status.OK) return;

                this.reset(model);
            })

        this.charsControl.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe((arr: Characteristic[]) => {
                this.compileDataPoints(arr, IndexDataPointType.CHARACTERISTIC);
            });

        this.typeControl.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe((value: string) => {
                if (value === 'radar') this.dataPointsArray.setValidators([Validators.required, Validators.minLength(3), Validators.maxLength(10)]);
                if (value === 'bar') this.dataPointsArray.setValidators([Validators.required, Validators.maxLength(10)]);
            });

        // TODO readd after adding indexes
        // this.indexesControl.valueChanges
        //     .pipe(takeUntil(this._destroy$))
        //     .subscribe((arr: string[]) => {
        //         this.compileDataPoints(arr, IndexDataPointType.INDEX);
        //     });
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    reset(model: ReportIndexFormStateModel) {
        this.labelControl.reset(model.data?.label || null, { emitEvent: false});
        this.typeControl.reset(model.data?.type || 'radar', { emitEvent: true});
        this.contextControl.reset(model.data?.context || null, { emitEvent: false});

        model.data?.dataPoints?.forEach(dataPoint => {
            let values = []
            this.addDataPointGroup(dataPoint.id, dataPoint.type, dataPoint.weight, dataPoint.isNegative);

            if (dataPoint.type === IndexDataPointType.CHARACTERISTIC) {
                values = this.charsControl.value;
                let char = this._library.chars.get(dataPoint.id);
                this.charsControl.patchValue([...values, char], { emitEvent: false });
            }

            if (dataPoint.type === IndexDataPointType.INDEX) {
                values = this.indexesControl.value;
                this.indexesControl.patchValue([...values, dataPoint.id], { emitEvent: false });
            }
        });
    }

    negativeItemClass = (control: AbstractControl): string | null => control.get('isNegative')?.value ? 'is-negative' : null;

    compileDataPoints(values: Characteristic[], type: IndexDataPointType) {
        let formData: IndexDataPoint[] = this.dataPointsArray.value;
        let newData: Characteristic[] = values.filter(val => !formData.map(data => data.id).includes(val.id));

        //? FORM ARRAY MOCKUPS
        let deletedDataPoints: IndexDataPoint[] = this.dataPointsArray.value.filter(data => data.type === type).filter(data => !values.map(val => val.id).includes(data.id));

        newData.forEach(value => {
            this.addDataPointGroup(value.id, type);
        });

        deletedDataPoints.forEach(dataPoint => {
            let data: IndexDataPoint[] = this.dataPointsArray.value;
            let index = data.findIndex(val => val.id === dataPoint.id);

            if (index < 0) return;

            this.dataPointsArray.removeAt(index);
        });
    }

    addDataPointGroup(id: string, type: IndexDataPointType, weight: number = 100, isNegative: boolean = false) {
        this.dataPointsArray.push(new FormGroup({
            id: new FormControl(id),
            type: new FormControl(type),
            weight: new FormControl(weight, [Validators.required, Validators.min(1), Validators.max(100)]),
            isNegative: new FormControl(isNegative),
        }));
    }

    patchIndexConfig() {
        this.configControl.patchValue(this.getIndexConfig());
    }

    getIndexConfig() {
        return getDefaultIndexConfig(this.typeControl.value);
    }

    setConfigLabels() {

    }

    setFormValue(control: FormControl, value: any) {
        control.patchValue(value);
    }

    attempt() {
        this.patchIndexConfig();

        this.formGroup.updateValueAndValidity();
        this.formGroup.markAsDirty();
        this.formGroup.markAllAsTouched();

        if (!this.dataPointsArray.valid) return this._snackbar.error('Your index requires at least 3 data points and a max of 10');
        if (!this.formGroup.valid) return this._snackbar.error('Invalid data, check your inputs and try again.');

        const {
            label,
            type,
            context,
            dataPoints,
            indexConfig,
        } = this.formGroup.value;

        const data: IndexComponentData = {
            ...this._data.data,
            label,
            type,
            context,
            dataPoints,
            indexConfig: {
                ...setIndexConfigOptions(type, indexConfig, label)
            }
        };

        this._dialogRef.close({
            status: true,
            data: data
        });
    }

    cancel() {
        this._dialogRef.close({status: false});
    }

    private getChartIconString(id: string) {
        if (id === "bar") return "bar_chart_4_bars"
        return "graph_5"
    }

    private toTitleCase(str: string): string {
        return str.replace(
          /\w\S*/g,
          text => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
        );
    }
}